'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Icon, Button } from '@benzinga/core-ui';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { Splide, SplideSlide, SplideTrack, Options } from '@splidejs/react-splide';
import cn from 'classnames';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';

export interface CarouselAnalytics {
  carouselName: string;
  category: string;
}

export interface CarouselProps {
  analytics?: CarouselAnalytics;
  component: (props: any) => React.ReactElement;
  items: any[];
  options?: Options;
  embeddedTitle?: React.ReactElement;
  className?: string;
  trackWrapperClassName?: string;
  paginationClassName?: string;
  embeddedTitleClassName?: string;
  itemClassName?: string;
  roundedBorder?: boolean;
  showArrowsOnlyOnOverflow?: boolean;
  showPagination?: boolean;
  arrowsPosition?: CarouselArrowPosition;
}

export type CarouselArrowPosition = 'on-block-arrows' | 'right' | 'center';

export const Carousel: React.FC<CarouselProps> = ({
  analytics,
  arrowsPosition = 'right',
  className = '',
  component: Component,
  embeddedTitle = '',
  embeddedTitleClassName,
  itemClassName,
  items,
  options = {},
  paginationClassName,
  roundedBorder = false,
  showArrowsOnlyOnOverflow,
  showPagination = true,
  trackWrapperClassName,
}) => {
  const session = React.useContext(SessionContext);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const splideRef = React.useRef<any>(null);
  const [hasOverflowingItems, setHasOverflowingItems] = React.useState(false);

  const checkIsOverflow = React.useCallback((): boolean => {
    let result = false;
    let totalWidth = 0;

    const containerElement = containerRef && containerRef?.current;
    if (containerElement && showArrowsOnlyOnOverflow) {
      const containerWidth = containerElement.clientWidth;

      Array.from(containerElement.querySelectorAll('.splide__slide')).forEach(element => {
        const box = element.getBoundingClientRect();
        totalWidth = totalWidth + box.width;
        if (box.left < 0 || totalWidth > containerWidth) {
          result = true;
        }
      });
      return result;
    }
    return result;
  }, [showArrowsOnlyOnOverflow]);

  React.useEffect(() => {
    const result = checkIsOverflow();
    setHasOverflowingItems(result);
  }, [checkIsOverflow]);

  const showArrows = React.useMemo(() => {
    return showArrowsOnlyOnOverflow && hasOverflowingItems
      ? true
      : showArrowsOnlyOnOverflow && !hasOverflowingItems
        ? false
        : typeof options.arrows === 'boolean'
          ? options.arrows
          : false;
  }, [hasOverflowingItems, options?.arrows, showArrowsOnlyOnOverflow]);

  const onResize = React.useCallback(() => {
    const result = checkIsOverflow();
    setHasOverflowingItems(result);
  }, [checkIsOverflow]);

  const classNames = cn('carousel-container', {
    [`${className}`]: !!className,
    'center-arrows': arrowsPosition === 'center',
    'on-component-arrows': arrowsPosition === 'on-block-arrows',
  });

  const handleEvent = (_event: any, eventName: string) => {
    if (eventName === 'onMove') {
      if (analytics) {
        trackAction(analytics.carouselName, analytics.category);
      }
    }
  };

  const trackAction = (name: string, category: string): void => {
    session.getManager(TrackingManager).trackPageEvent('view', {
      page_section: `carousel_${category}`,
      page_tab: name,
    });
  };

  const goPrevious = () => {
    splideRef.current?.splide?.go('<');
  };

  const goNext = () => {
    splideRef.current?.splide?.go('>');
  };

  return (
    <Container className={classNames} ref={containerRef} roundedBorder={roundedBorder}>
      <Splide
        aria-label="Carousel"
        hasTrack={false}
        onClick={event => handleEvent(event, 'onClick')}
        onMove={event => handleEvent(event, 'onMove')}
        onResize={onResize}
        options={{
          autoWidth: true,
          drag: 'free',
          focus: 'center',
          gap: '1rem',
          pagination: false,
          perPage: options?.perPage ?? 2,
          trimSpace: 'move',
          ...options,
          arrows: arrowsPosition === 'on-block-arrows' ? false : showArrows,
          lazyLoad: false,
        }}
        ref={splideRef}
      >
        <div className={cn('splide-track-wrapper', trackWrapperClassName)}>
          <SplideTrack>
            {items.map((item, index) => (
              <SplideSlide key={index}>
                {itemClassName ? (
                  <div className={itemClassName}>
                    <Component {...item} />
                  </div>
                ) : (
                  <Component {...item} />
                )}
              </SplideSlide>
            ))}
          </SplideTrack>
          {showPagination && arrowsPosition !== 'on-block-arrows' && (
            <div className={cn('carousel-pagination', paginationClassName)}>
              {embeddedTitle && <div className={cn('embedded-title', embeddedTitleClassName)}>{embeddedTitle}</div>}
              <div className="splide__arrows">
                <Button
                  className="splide__arrow splide__arrow--prev page-left-button"
                  onClick={goPrevious}
                  variant="primary"
                >
                  <Icon icon={faChevronLeft} />
                </Button>
                <Button
                  className="splide__arrow splide__arrow--next page-right-button"
                  onClick={goNext}
                  variant="primary"
                >
                  <Icon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Splide>
      {showPagination && showArrows && arrowsPosition === 'on-block-arrows' && (
        <div className="on-component-arrows-container">
          <Button className="splide__arrow splide__arrow--prev" onClick={goPrevious} variant="primary">
            <Icon icon={faChevronLeft} />
          </Button>
          <Button className="splide__arrow splide__arrow--next" onClick={goNext} variant="primary">
            <Icon icon={faChevronRight} />
          </Button>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div<{ roundedBorder: boolean }>`
  &.carousel-container {
    overflow: hidden;
    border-radius: ${({ roundedBorder }) => (roundedBorder ? '8px' : '0')};

    .splide__list {
      backface-visibility: hidden;
      display: -ms-flexbox;
      display: flex;
      height: 100%;
      margin: 0 !important;
      padding: 0 !important;
    }

    .splide__sr {
      display: none !important;
      visibility: hidden;
    }

    .carousel-pagination {
      display: flex;
      width: 100%;

      .splide__arrows {
        display: flex;
        margin-left: auto;

        > span {
          font-size: 20px;
        }
      }

      button {
        width: 50px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.page-left-button {
          margin-right: 10px;
        }
      }
    }

    &.center-arrows {
      .carousel-pagination {
        display: block;
        text-align: center;
        margin-top: 12px;
        margin-bottom: 24px;

        .splide__arrows {
          align-items: center;
          justify-content: center;

          button {
            width: 90px;
            height: 32px;
          }
        }
      }
    }

    &.on-component-arrows {
      position: relative;

      .carousel-pagination {
        display: none;
      }

      .on-component-arrows-container {
        .splide__arrow--prev {
          position: absolute;
          top: 0;
          left: 0;
        }

        .splide__arrow--next {
          position: absolute;
          top: 0;
          right: 0;
        }
        .splide__arrow {
          background: rgba(0, 0, 0, 0.5);
          color: #fff;
          border: none;
          width: 20px;
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
        }
      }
    }
  }
`;
